import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getUsers, UserListResponse } from "../../../../api/user";

interface UseUserListParams {
  initialPage?: number;
  initialLimit?: number;
  isAdmin?: boolean;
}

export const useUserList = ({
  initialPage = 1,
  initialLimit = 10,
  isAdmin = true,
}: UseUserListParams) => {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input so API calls are not triggered on every key press.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  // Reset page to 1 whenever search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const responseQuery = useQuery<UserListResponse>({
    queryKey: ["admin-users", { page, limit, search: debouncedSearch }],
    queryFn: () =>
      getUsers(
        { page, limit, ...(debouncedSearch ? { search: debouncedSearch } : {}) },
        isAdmin,
      ),
  });

  const onPaginationChange = (pagination: {
    current: number;
    pageSize: number;
  }) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
  };

  return {
    ...responseQuery,
    page,
    limit,
    search,
    setSearch,
    onPaginationChange,
  };
};
